import type { AuthenticatedUserContract, Locale } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Button } from "@chams-pay/design-system/components/button";
import { cn } from "@chams-pay/design-system/lib/utils";
import {
  ChevronDown,
  Languages,
  Loader2,
  Lock,
  LogOut,
  PanelLeft,
  RefreshCw,
  Settings2,
  TriangleAlert,
  WifiOff,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatStamp, initialOf } from "../core/format.js";
import { deploymentProductName } from "../core/deployment.js";
import type { WorkspaceNavItem, WorkspaceScreen } from "../core/navigation.js";
import type { WorkspaceBannerKind, WorkspaceController } from "../core/use-workspace.js";
import { relativeAgeLabel } from "../core/use-workspace.js";
import { AppIcon, Wordmark } from "../ui/brand.js";
import { Bidi, Count } from "../ui/figures.js";

type MessageKey = Parameters<typeof translate>[1];

const dotToneClass: Record<"green" | "amber" | "red", string> = {
  green: "bg-success shadow-[0_0_0_3px_rgb(31_122_77/0.16)]",
  amber: "bg-warning shadow-[0_0_0_3px_rgb(168_107_18/0.16)]",
  red: "bg-destructive shadow-[0_0_0_3px_rgb(179_38_30/0.16)]",
};

export function LanguageSwitch({
  locale,
  onChange,
}: {
  readonly locale: Locale;
  readonly onChange: () => void;
}) {
  const label = locale === "ar" ? "Français" : "العربية";
  return (
    <Button
      className="floating-language-switch h-11 min-h-11 px-3 text-xs"
      variant="ghost"
      onClick={onChange}
      aria-label={label}
    >
      <Languages />
      {label}
    </Button>
  );
}

/* ------------------------------------------------------------------ */
/* Account and synchronization popover (screen 15)                     */
/* ------------------------------------------------------------------ */

function SyncPanelBody({
  locale,
  workspace,
  online,
  onSettings,
  onLock,
  onLogout,
}: {
  readonly locale: Locale;
  readonly workspace: WorkspaceController;
  readonly online: boolean;
  readonly onSettings: () => void;
  readonly onLock: () => void;
  readonly onLogout: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const since = relativeAgeLabel(workspace.syncAgeMilliseconds, locale);
  return (
    <div className="flex flex-col gap-3.5 p-4">
      <div
        data-online={online}
        className="flex items-center gap-2.5 rounded-md border border-border bg-sunken px-3.5 py-2.5 data-[online=true]:border-success-border data-[online=true]:bg-success-soft"
      >
        <span
          aria-hidden="true"
          className={cn(
            "size-2 shrink-0 rounded-full",
            online ? dotToneClass[workspace.syncDot] : "bg-muted-foreground",
          )}
        />
        <span
          className={cn(
            "font-heading text-xs font-bold",
            online ? "text-success" : "text-muted-foreground",
          )}
        >
          {t(online ? "dash.online" : "dash.offline")}
        </span>
        <span className="ms-auto flex flex-col items-end gap-0.5 text-end text-[11px] text-muted-foreground">
          <span>{t("dash.lastSync")}</span>
          <Bidi className="text-[10.5px]">{since}</Bidi>
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {t("dash.pendingPayments")}
          <Count value={workspace.pendingCount} className="text-sm font-bold text-foreground" />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {t("dash.localCopy")}
          <span className="chams-figure text-xs text-muted-foreground">
            <Bidi>
              {String(workspace.replica.students.length)} ·{" "}
              {formatStamp(workspace.replica.synchronizedAt, locale)}
            </Bidi>
          </span>
        </div>
      </div>

      <Button
        className="w-full"
        disabled={!workspace.canSync}
        onClick={() => void workspace.sync()}
      >
        <RefreshCw className={workspace.syncing ? "animate-spin" : undefined} />
        {t("dash.syncNow")}
      </Button>

      {workspace.readinessTier === "warning" && (
        <p className="rounded-md border border-warning-border bg-warning-soft px-3.5 py-2.5 text-[11px] leading-relaxed text-warning">
          {t("dash.readinessWarn")}
        </p>
      )}

      <div className="h-px bg-border" />

      <nav className="flex flex-col gap-0.5" aria-label={t("dash.workspace")}>
        <button
          type="button"
          onClick={onSettings}
          className="chams-focusable flex h-10 items-center gap-2.5 rounded-md px-2.5 font-heading text-xs font-semibold text-muted-foreground transition-colors duration-fast hover:bg-sunken hover:text-foreground"
        >
          <Settings2 aria-hidden="true" className="size-4" />
          {t("dash.nav5")}
        </button>
        <button
          type="button"
          onClick={onLock}
          className="chams-focusable flex h-10 items-center gap-2.5 rounded-md px-2.5 font-heading text-xs font-semibold text-muted-foreground transition-colors duration-fast hover:bg-sunken hover:text-foreground"
        >
          <Lock aria-hidden="true" className="size-4" />
          {t("dash.lock")}
        </button>
        <button
          type="button"
          onClick={onLogout}
          className="chams-focusable flex h-10 items-center gap-2.5 rounded-md px-2.5 font-heading text-xs font-semibold text-destructive transition-colors duration-fast hover:bg-destructive-soft"
        >
          <LogOut aria-hidden="true" className="size-4" />
          {t("dash.signOut")}
        </button>
      </nav>
    </div>
  );
}

function AccountPopover({
  locale,
  user,
  online,
  workspace,
  collapsed,
  onRequestExpand,
  onSettings,
  onLock,
  onLogout,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly online: boolean;
  readonly workspace: WorkspaceController;
  readonly collapsed: boolean;
  readonly onRequestExpand: () => void;
  readonly onSettings: () => void;
  readonly onLock: () => void;
  readonly onLogout: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const displayName = user
    ? locale === "ar"
      ? user.displayNameAr
      : user.displayNameFr
    : deploymentProductName;
  const role = t(
    user?.role === "system-administrator"
      ? "profile.systemAdministrator"
      : "profile.financialAdministrator",
  );

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: Event) => {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={anchor} className="relative mt-auto pt-3">
      <button
        type="button"
        aria-expanded={open}
        aria-label={t("profile.title")}
        title={collapsed ? displayName : undefined}
        onClick={() => {
          if (collapsed) onRequestExpand();
          setOpen((value) => !value);
        }}
        className={cn(
          "sidebar-profile-trigger chams-focusable flex h-[52px] w-full items-center gap-2.5 rounded-md border border-border bg-card text-start shadow-e1 transition-colors duration-fast hover:border-teal-200",
          collapsed ? "justify-center px-2" : "px-2.5",
        )}
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft font-heading text-xs font-bold text-primary-strong">
          {initialOf(displayName)}
        </span>
        {!collapsed && (
          <>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-heading text-xs font-bold">{displayName}</span>
              <span className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-[7px] shrink-0 rounded-full",
                    online ? dotToneClass[workspace.syncDot] : "bg-muted-foreground",
                  )}
                />
                <span>
                  {t(online ? "dash.online" : "dash.offline")} ·{" "}
                  <Bidi>{relativeAgeLabel(workspace.syncAgeMilliseconds, locale)}</Bidi>
                </span>
              </span>
            </span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "ms-auto size-3.5 shrink-0 text-muted-2 transition-transform",
                open && "rotate-180",
              )}
            />
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="profile-sheet"
          role="dialog"
          aria-label={t("profile.title")}
          className="absolute inset-x-0 bottom-0 z-50 max-h-[calc(100dvh-1.75rem)] w-full overflow-y-auto rounded-lg border border-border bg-popover shadow-e4 animate-fade-up [animation-duration:160ms]"
        >
          <div className="flex items-center gap-2.5 border-b border-border px-4 py-3.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-soft font-heading text-[13px] font-bold text-primary-strong">
              {initialOf(displayName)}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-heading text-[13px] font-bold">{displayName}</span>
              <span className="truncate text-[11px] text-muted-foreground">{role}</span>
            </span>
            <button
              type="button"
              aria-label={t("common.close")}
              onClick={() => setOpen(false)}
              className="chams-focusable ms-auto grid size-7 place-items-center rounded-sm bg-sunken text-muted-foreground transition-colors duration-fast hover:text-foreground"
            >
              <X aria-hidden="true" className="size-3.5" />
            </button>
          </div>
          <SyncPanelBody
            locale={locale}
            workspace={workspace}
            online={online}
            onSettings={() => {
              setOpen(false);
              onSettings();
            }}
            onLock={() => {
              setOpen(false);
              onLock();
            }}
            onLogout={() => {
              setOpen(false);
              onLogout();
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sidebar                                                             */
/* ------------------------------------------------------------------ */

export function AppSidebar({
  locale,
  user,
  items,
  activeScreen,
  online,
  workspace,
  instrumentCount,
  collapsed,
  onCollapsedChange,
  onSelect,
  onLock,
  onLogout,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly items: readonly WorkspaceNavItem[];
  readonly activeScreen: WorkspaceScreen;
  readonly online: boolean;
  readonly workspace: WorkspaceController;
  readonly instrumentCount: number;
  readonly collapsed: boolean;
  readonly onCollapsedChange: (collapsed: boolean) => void;
  readonly onSelect: (screen: WorkspaceScreen) => void;
  readonly onLock: () => void;
  readonly onLogout: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        "shell-sidebar relative flex shrink-0 flex-col bg-canvas px-3 py-3.5 transition-[width] duration-base",
        collapsed ? "w-[76px]" : "w-64",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2.5 pb-4 pt-2",
          collapsed ? "justify-center px-0" : "px-2.5",
        )}
      >
        <AppIcon className="size-[34px]" />
        {!collapsed && (
          <span className="brand-copy flex min-w-0 flex-1 flex-col items-start gap-px">
            <Wordmark className="text-sm" />
            <span className="truncate text-[10.5px] text-muted-2">{t("dash.schoolName")}</span>
          </span>
        )}
      </div>

      <nav aria-label={t("app.brand")} className="flex flex-col gap-0.5">
        {items.map((item) => {
          const Glyph = item.icon;
          const active = activeScreen === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              aria-current={active ? "page" : undefined}
              title={collapsed ? item.title : undefined}
              className={cn(
                "chams-focusable relative flex h-10 items-center gap-[11px] rounded-md font-heading text-[13px] transition-colors duration-fast",
                collapsed ? "justify-center px-0" : "px-2.5",
                active
                  ? "bg-card font-bold text-foreground shadow-e1"
                  : "font-semibold text-muted-foreground hover:bg-sunken hover:text-foreground",
              )}
            >
              <Glyph
                aria-hidden="true"
                strokeWidth={1.75}
                className={cn("size-[18px] shrink-0", active ? "text-primary" : "text-muted-2")}
              />
              {!collapsed && <span className="truncate">{item.title}</span>}
              {!collapsed && item.id === "instruments" && instrumentCount > 0 && (
                <span className="chams-figure ms-auto grid h-5 min-w-5 place-items-center rounded-full bg-warning-soft px-1.5 text-[11px] font-bold text-warning">
                  {String(instrumentCount)}
                </span>
              )}
              {active && (
                <span
                  aria-hidden="true"
                  className="absolute end-1 h-4 w-[3px] rounded-[2px] bg-primary"
                />
              )}
            </button>
          );
        })}
      </nav>

      <AccountPopover
        locale={locale}
        user={user}
        online={online}
        workspace={workspace}
        collapsed={collapsed}
        onRequestExpand={() => onCollapsedChange(false)}
        onSettings={() => onSelect("settings")}
        onLock={onLock}
        onLogout={onLogout}
      />
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* Sticky card header                                                  */
/* ------------------------------------------------------------------ */

export function SiteHeader({
  title,
  subtitle,
  actions,
  sidebarCollapsed,
  sidebarToggleLabel,
  onSidebarToggle,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly actions: React.ReactNode;
  readonly sidebarCollapsed: boolean;
  readonly sidebarToggleLabel: string;
  readonly onSidebarToggle: () => void;
}) {
  return (
    <header className="shell-header sticky top-0 z-20 flex flex-none items-center gap-[18px] border-b border-sunken bg-card px-5 py-4 shadow-[0_2px_5px_-3px_rgb(16_28_30/0.08)]">
      <button
        type="button"
        aria-label={sidebarToggleLabel}
        aria-expanded={!sidebarCollapsed}
        onClick={onSidebarToggle}
        className="chams-focusable grid size-[30px] shrink-0 place-items-center rounded-sm text-muted-2 transition-colors duration-fast hover:bg-sunken hover:text-foreground"
      >
        <PanelLeft
          aria-hidden="true"
          className={cn(
            "size-4 transition-transform rtl:-scale-x-100",
            sidebarCollapsed && "ltr:rotate-180 rtl:rotate-180",
          )}
        />
      </button>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h1 className="truncate font-heading text-xl font-bold">{title}</h1>
        {subtitle && <span className="truncate text-[11.5px] text-muted-2">{subtitle}</span>}
      </div>
      <div className="header-actions ms-auto flex items-center gap-2.5">{actions}</div>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Single-banner slot                                                  */
/* ------------------------------------------------------------------ */

export function WorkspaceBanner({
  locale,
  kind,
  workspace,
  updateActivating,
  onActivateUpdate,
}: {
  readonly locale: Locale;
  readonly kind: WorkspaceBannerKind;
  readonly workspace: WorkspaceController;
  readonly updateActivating: boolean;
  readonly onActivateUpdate: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  if (!kind) return null;

  if (kind === "capture-blocked") {
    const storageBlocked = workspace.captureBlockReason === "storage";
    const clockBlocked = workspace.captureBlockReason === "clock";
    const titleKey = storageBlocked
      ? "dash.storageBlockedTitle"
      : clockBlocked
        ? "dash.clockBlockedTitle"
        : "dash.blockedTitle";
    const bodyKey = storageBlocked
      ? "dash.storageBlockedBody"
      : clockBlocked
        ? "dash.clockBlockedBody"
        : "dash.blockedBody";
    return (
      <div
        role="alert"
        className="workspace-status-banner flex items-start gap-3 rounded-md bg-destructive px-4 py-4 shadow-[0_6px_16px_-6px_rgb(179_38_30/0.5)]"
      >
        <span
          aria-hidden="true"
          className="grid size-5 shrink-0 place-items-center rounded-full bg-white/20 font-heading text-[13px] font-extrabold text-white"
        >
          !
        </span>
        <span className="flex flex-col gap-1">
          <strong className="font-heading text-[13.5px] text-white">{t(titleKey)}</strong>
          <span className="text-xs leading-relaxed text-white/85">{t(bodyKey)}</span>
        </span>
        <Button
          variant="outline"
          size="sm"
          className="ms-auto shrink-0 border-transparent text-destructive"
          disabled={!workspace.canSync}
          onClick={() => void workspace.sync()}
        >
          <RefreshCw className={workspace.syncing ? "animate-spin" : undefined} />
          {t("dash.syncNow")}
        </Button>
      </div>
    );
  }

  if (kind === "offline") {
    return (
      <p
        role="status"
        className="workspace-status-banner flex items-center gap-2.5 rounded-md bg-[#5d6b6b] px-4 py-2.5 text-sm text-white"
      >
        <WifiOff aria-hidden="true" className="size-4 shrink-0" />
        {t("ops.bannerOffline")}
        <span className="ms-auto text-xs text-white/80">
          {t("dash.lastSync")}{" "}
          <Bidi>{relativeAgeLabel(workspace.syncAgeMilliseconds, locale)}</Bidi>
        </span>
      </p>
    );
  }

  if (kind === "sync-failed") {
    return (
      <div
        role="alert"
        className="workspace-status-banner flex items-start gap-2.5 rounded-md border border-destructive-border bg-destructive-soft px-4 py-3"
      >
        <TriangleAlert aria-hidden="true" className="size-4 shrink-0 text-destructive" />
        <span className="flex flex-col gap-1">
          <strong className="font-heading text-xs text-destructive">
            {t("ops.bannerSyncFailed")}
          </strong>
          <span className="text-[11.5px] leading-relaxed text-destructive">
            {t("ops.bannerSyncFailedBody")}
          </span>
        </span>
        <Button
          variant="destructive"
          size="sm"
          className="ms-auto shrink-0"
          disabled={!workspace.canSync}
          onClick={() => void workspace.sync()}
        >
          {t("ops.retry")}
        </Button>
      </div>
    );
  }

  if (kind === "update-after-sync") {
    return (
      <p
        role="status"
        className="workspace-status-banner flex items-center gap-2.5 rounded-md border border-info-border bg-info-soft px-4 py-3 text-xs text-info"
      >
        <Loader2 aria-hidden="true" className="size-4 shrink-0 animate-spin" />
        <span className="flex flex-col gap-0.5">
          <strong className="font-heading">{t("ops.bannerUpdateAfterSync")}</strong>
          <span className="text-[11.5px]">{t("ops.bannerUpdateAfterSyncBody")}</span>
        </span>
        <Count value={workspace.pendingCount} className="ms-auto font-bold" />
      </p>
    );
  }

  return (
    <div
      role="status"
      className="workspace-status-banner flex items-center gap-2.5 rounded-md border border-warning-border bg-warning-soft px-4 py-3 text-warning"
    >
      <span className="flex flex-col gap-0.5">
        <strong className="font-heading text-xs">{t("ops.bannerUpdate")}</strong>
        <span className="text-[11.5px]">{t("ops.bannerUpdateBody")}</span>
      </span>
      <Button
        variant="outline"
        size="sm"
        className="ms-auto shrink-0 border-warning-border text-warning"
        disabled={updateActivating}
        onClick={onActivateUpdate}
      >
        {updateActivating && <Loader2 aria-hidden="true" className="animate-spin" />}
        {t("ops.restartNow")}
      </Button>
    </div>
  );
}
